import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { hasAnyRole } from "@/lib/roles";
import { useIsMobile } from "@/hooks/useMobile";
import { Dumbbell, Users, Calendar, Clock, TrendingUp, AlertCircle, QrCode, Printer } from "lucide-react";
import { useLocation } from "wouter";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { useState, CSSProperties } from "react";

export default function GymDashboard() {
  const { loading, user, logout } = useAuth();
  const { data: config } = trpc.config.getTenantConfig.useQuery();
  const [, setLocation] = useLocation();
  const isMobile = useIsMobile();
  
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  
  // Get today's slots
  const today = new Date();
  const startOfDay = new Date(today.setHours(0, 0, 0, 0));
  const endOfDay = new Date(today.setHours(23, 59, 59, 999));
  
  const { data: todaySlots } = trpc.gym.slots.list.useQuery({
    startDate: startOfDay.toISOString(),
    endDate: endOfDay.toISOString()
  });
  
  const { data: services } = trpc.gym.services.list.useQuery({});
  const { data: pendingMembers } = trpc.gym.members.pending.useQuery();
  
  // Calculate stats
  const totalTodaySlots = todaySlots?.length ?? 0;
  const activeServices = services?.filter(s => s.isActive).length ?? 0;
  const pendingCount = pendingMembers?.length ?? 0;

  // Auth check
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Laden...</div>;
  }

  if (!user || !hasAnyRole(user.roles, ["admin", "courseadmin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <h1 className="text-2xl font-semibold tracking-tight text-center">
            Anmeldung erforderlich
          </h1>
          <p className="text-sm text-muted-foreground text-center max-w-sm">
            Der Zugriff auf diesen Bereich erfordert Administratorrechte.
          </p>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl(config?.authProvider);
            }}
            size="lg"
            className="w-full"
          >
            Anmelden
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}
    >
      <AdminSidebar setSidebarWidth={setSidebarWidth} />
      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">Fitnessstudio</span>
            </div>
          </div>
        )}
        <main className="flex-1 p-4">
          <div className="space-y-6">
            {/* Header */}
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Fitnessstudio</h1>
              <p className="text-muted-foreground">
                Verwalten Sie Ihr Fitnessstudio - Services, Slots und Mitglieder
              </p>
            </div>

            {/* Alert for pending members */}
            {pendingCount > 0 && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="flex items-center gap-4 py-4">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  <div className="flex-1">
                    <p className="font-medium text-orange-800">
                      {pendingCount} neue Mitgliedsanfrage{pendingCount > 1 ? "n" : ""}
                    </p>
                    <p className="text-sm text-orange-600">
                      Bitte überprüfen und genehmigen Sie die Anfragen
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    className="border-orange-300 text-orange-700 hover:bg-orange-100"
                    onClick={() => setLocation("/admin/gym/members")}
                  >
                    Anzeigen
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Stats Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Heutige Slots</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{totalTodaySlots}</div>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(), "EEEE, d. MMMM", { locale: de })}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Aktive Services</CardTitle>
                  <Dumbbell className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{activeServices}</div>
                  <p className="text-xs text-muted-foreground">
                    von {services?.length ?? 0} Services
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Ausstehende Anfragen</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{pendingCount}</div>
                  <p className="text-xs text-muted-foreground">
                    Mitgliedsanfragen
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Auslastung</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">--</div>
                  <p className="text-xs text-muted-foreground">
                    Durchschnitt heute
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => window.open('/api/gym/check-in-poster', '_blank')}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <QrCode className="h-5 w-5 text-primary" />
                    Check-in Poster
                  </CardTitle>
                  <CardDescription>
                    QR-Code Poster für Self-Service Check-in generieren und drucken
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setLocation("/admin/gym/services")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Dumbbell className="h-5 w-5 text-primary" />
                    Services verwalten
                  </CardTitle>
                  <CardDescription>
                    Erstellen und bearbeiten Sie Trainingsarten wie Kraft, Ausdauer, etc.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setLocation("/admin/gym/slots")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary" />
                    Slot-Kalender
                  </CardTitle>
                  <CardDescription>
                    Verwalten Sie verfügbare Zeitslots und Buchungen
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setLocation("/admin/gym/members")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Mitglieder
                  </CardTitle>
                  <CardDescription>
                    Verwalten Sie Gym-Mitglieder und genehmigen Sie Anfragen
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>

            {/* Today's Slots */}
            {todaySlots && todaySlots.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Heutige Slots</CardTitle>
                  <CardDescription>
                    Übersicht der Slots für heute
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {todaySlots.slice(0, 5).map(slot => (
                      <div 
                        key={slot.id} 
                        className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => setLocation(`/admin/gym/bookings?slotId=${slot.id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: slot.service?.color ?? "#888" }}
                          />
                          <div>
                            <p className="font-medium">{slot.service?.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(slot.startTime), "HH:mm")} - {format(new Date(slot.endTime), "HH:mm")}
                            </p>
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {slot.bookingCount ?? 0} / {slot.maxParticipants} Plätze
                        </div>
                      </div>
                    ))}
                    {todaySlots.length > 5 && (
                      <Button 
                        variant="ghost" 
                        className="w-full"
                        onClick={() => setLocation("/admin/gym/slots")}
                      >
                        Alle {todaySlots.length} Slots anzeigen
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
